import { ref, watch, onUnmounted } from 'vue';

// Every overlay in the conversation view (copilot popup, schedule dialog,
// image/avatar preview, quoted-reply banner) used to listen for Escape
// independently — either via its own useKeyboardEvents({ Escape }) call, or
// (for Modal.vue-based popups) a document keydown listener with its own
// stopPropagation(). None of that stops OTHER independent listeners on the
// same `document` node: addEventListener fires every registered listener for
// a given target in registration order regardless of stopPropagation, which
// only affects bubbling to ANCESTOR nodes. So pressing Escape with, say, the
// copilot popup open fired both the popup's own close AND
// ConversationHeader's closeConversationView — the ticket closed right along
// with the popup.
//
// This module is a single shared registry: any overlay that wants to own
// Escape while it's open registers itself here (in open->close order, LIFO)
// for the duration it's visible. ConversationHeader's global Escape handler
// checks hasEscapePriorityOwner() first and no-ops if anything is registered
// — the still-independent per-overlay Escape handling (Modal.vue's built-in
// keydown listener, GalleryView's useKeyboardEvents, Dialog.vue's native
// <dialog> Escape-to-cancel) is left alone to actually close its own overlay.
// This registry's only job is to make the ticket-closer stand down.
const owners = ref([]);

export function useEscapePriority(isActiveRef) {
  const key = Symbol('escape-priority-owner');

  watch(
    isActiveRef,
    isActive => {
      if (isActive) {
        if (!owners.value.includes(key)) owners.value.push(key);
      } else {
        const idx = owners.value.indexOf(key);
        if (idx !== -1) owners.value.splice(idx, 1);
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    const idx = owners.value.indexOf(key);
    if (idx !== -1) owners.value.splice(idx, 1);
  });
}

export function hasEscapePriorityOwner() {
  return owners.value.length > 0;
}

// Imperative pair for Options API components whose open/closed state lives
// in `data()`/`computed` — not a ref `useEscapePriority` can `watch()` from
// setup(). Call registerEscapePriorityOwner(key) where the state becomes
// active and unregisterEscapePriorityOwner(key) where it stops (both watcher
// callback and the component's own unmounted/beforeUnmount hook, in case it
// unmounts while still active). `key` just needs to be stable and unique per
// call site — a string literal is fine since callers don't nest.
export function registerEscapePriorityOwner(key) {
  if (!owners.value.includes(key)) owners.value.push(key);
}

export function unregisterEscapePriorityOwner(key) {
  const idx = owners.value.indexOf(key);
  if (idx !== -1) owners.value.splice(idx, 1);
}
